// UTILS
import { nextTick } from "vue"
import { setup, createActor, fromPromise } from "xstate"
import { storeToRefs } from "pinia"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"

// TYPES
import type { BrainMachineEventType } from "./types"
import { type GlobalMachineEvent } from "@/machines/types"

// STORES
import { useAnswersStore } from "@/features/brain/stores/answers.store"
import { useUserStore } from "@/stores/user"

// MACHINE
import { globalActor } from "@/machines/globalMachine"

// HELPERS
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useHelpStore } from "@/components/af/Help/help.store"

// Loader for brain data
// to redirect to Intro or WelcomeBack
const loadBrainData = fromPromise(async () => {
  const { getBrainStatus } = storeToRefs(useUserStore())
  const { getBrain, initBrain, fetchAudios } = useAnswersStore()
  const { totalAnswers } = storeToRefs(useAnswersStore())

  let data
  if (getBrainStatus.value === null) {
    await initBrain()
    await fetchAudios()
    return { status: "new" }
  } else {
    data = await getBrain()
    await fetchAudios()

    const assessmentSummary = useUserStore().assessmentSummary.assessments.brain
    const isFinisedAndNotPassed =
      data?.status === "finish" && assessmentSummary?.not_passed

    // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
    let assessmentStatus = isFinisedAndNotPassed ? "todo" : data?.status

    switch (assessmentStatus) {
      case "todo":
        return { status: "new" }

      case "inprogress":
        // le test n'est pas terminé si moins de 10 réponses
        if (totalAnswers.value < 10) {
          return { status: "showQuestions" }
        }
        // If there is no wholesome conditions, we need to show the wholesome conditions screen
        else if (
          data.data.extra?.native_language == undefined ||
          data.data.extra?.native_language == null
        ) {
          return { status: "showNativeLanguage" }
        }
        // If there is no wholesome conditions, we need to show the wholesome conditions screen
        else if (
          data.data.extra?.wholesome_conditions == undefined ||
          data.data.extra?.wholesome_conditions == null
        ) {
          return { status: "showWholesomeConditions" }
        }
        // If the user is restarting the test, we need to show the restart screen
        else if (
          data.data.extra?.wholesome_conditions == false &&
          (data.data.extra?.isRestarting == undefined ||
            data.data.extra?.isRestarting != true)
        ) {
          return { status: "showRestart" }
        }
        // If the user has complete every steps but status is inprogress, we need to send the results to backend
        else {
          return { status: "showLoadResults" }
        }

      case "redirect-contact":
        globalActor.send({ type: "GLOBAL.GUEST.CONTACTSUPPORT" })
        return

      case "thank-you":
        router.replace({ name: "thanksATS" })
        return

      case "finish":
      default:
        return { status: "final" }
    }
  }
})

export const brainMachine = setup({
  actors: {
    loadBrainData,
  },
  types: {} as {
    events: BrainMachineEventType
  },
  actions: {},
}).createMachine({
  id: "brainMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "brainLoading") {
            router.replace({ name: "brainLoading" })
          }
        },
      ],
      invoke: {
        id: "loadBrain",
        src: "loadBrainData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "intro",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showNativeLanguage",
            target: "nativeLanguage",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showWholesomeConditions",
            target: "wholesomeConditions",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showRestart",
            target: "restart",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    intro: {
      entry: [() => router.replace({ name: "brainIntro" })],
      on: {
        done: {
          target: "rules",
        },
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "brainWelcomeBack" })],
      on: {
        done: {
          target: "resume",
        },
      },
    },
    rules: {
      entry: [() => router.replace({ name: "brainRules" })],
      on: {
        done: {
          target: "practice",
        },
        back: {
          target: "intro",
        },
      },
    },
    practice: {
      entry: [() => router.replace({ name: "brainPractice" })],
      on: {
        done: {
          target: "tuto",
        },
        back: {
          target: "rules",
        },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "brainTuto" })],
      on: {
        done: {
          target: "tutoConfirmation",
        },
      },
    },
    tutoConfirmation: {
      entry: [() => router.replace({ name: "brainTutoConfirmation" })],
      on: {
        done: {
          target: "breath",
        },
        restart: {
          target: "tuto",
        },
      },
    },
    resume: {
      entry: [() => router.replace({ name: "brainResume" })],
      on: {
        done: {
          target: "breath",
        },
      },
    },
    breath: {
      entry: [() => router.replace({ name: "brainBreath" })],
      on: {
        done: { target: "test" },
      },
    },
    test: {
      entry: [() => router.replace({ name: "brainTest" })],
      on: {
        done: {
          target: "nativeLanguage",
        },
      },
    },
    nativeLanguage: {
      entry: [() => router.replace({ name: "brainNativeLanguage" })],
      on: {
        done: { target: "wholesomeConditions" },
      },
    },
    wholesomeConditions: {
      entry: [() => router.replace({ name: "brainWholesomeConditions" })],
      on: {
        true: { target: "loadResults" },
        restart: { target: "restart" },
      },
    },
    restart: {
      entry: [() => router.replace({ name: "brainRestart" })],
      on: {
        now: { target: "loading" },
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "brainLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export const brainActor = createActor(brainMachine)

brainActor.subscribe({
  async complete() {
    const { isFromInt } = storeToRefs(useUserStore())
    const { getSessionItem } = useSessionStorage()

    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())
    const { af_pm_uuid, af_company_uuid, af_retake } =
      storeToRefs(useHelpStore())

    const pm_uuid = af_pm_uuid.value ?? useCookies().get(COOKIES.PM_UUID)
    const company_uuid =
      af_company_uuid.value ?? useCookies().get(COOKIES.COMPANY_UUID)
    const retake = af_retake.value ?? useCookies().get(COOKIES.RETAKE)

    await fetchNextAssessment(
      pm_uuid ?? null,
      company_uuid ?? null,
      retake ?? false
    )

    await nextTick()

    if (isFromInt.value && getSessionItem("goto_param")) {
      const didRedirect = await redirectForInt()
      if (didRedirect) return
    }

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.PM_UUID)
      useCookies().remove(COOKIES.COMPANY_UUID)
      useCookies().remove(COOKIES.RETAKE)
      af_pm_uuid.value = ""
      af_company_uuid.value = ""
      af_retake.value = false

      const didRedirect = await redirectForInt()
      if (didRedirect) return

      globalActor.send({ type: "GLOBAL.BRAIN.DONE" })
    }
  },
})

async function redirectForInt(): Promise<boolean> {
  const { isFromInt } = storeToRefs(useUserStore())
  const { getSessionItem, deleteSessionItem } = useSessionStorage()

  if (isFromInt.value) {
    if (getSessionItem("goto_param")) {
      deleteSessionItem("goto_param")
    }

    if (getSessionItem("redirect_url")) {
      let redirectUrl = getSessionItem("redirect_url") as string
      deleteSessionItem("redirect_url")
      // Redirect to custom page
      window.location.href = redirectUrl
      return true
    } else {
      // Redirect to thanksATS route
      await router.push({ name: "thanksATS" })
      return true
    }
  }

  return false
}
